/**
 * CustomerModalSidebar - Sidebar contextual do modal premium de Cliente
 * Posicionada à DIREITA, padronizada com DealModalSidebar v3.0.0 / TaskModalSidebar.
 *
 * @version 2.2.0
 * RELEASE: 2026-08-19 v2.2.0 - Ação rápida "Transformar em cliente" (modo Contato)
 * RELEASE: 2026-05-28 v2.0.0 - Padronização visual com DealModalSidebar (cards flutuantes)
 *   - Container `bg-muted/30 border-l border-border/40`, `space-y-3`
 *   - Cada seção dentro de `SectionCard` (rounded-xl border bg-card shadow-sm p-3)
 *   - Cabeçalhos com ícone-âncora à direita ou `action` (botão Pencil sempre visível)
 *   - Removidos todos os `<Separator />` (substituídos pelo agrupamento em cards)
 * RELEASE: 2026-01-10 v1.3.0 - Versão anterior com Separator entre seções
 */
import React, { useState } from 'react';
import { formatDocumentForDisplay } from '@/lib/input-masks';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Phone,
  Mail,
  Calendar,
  CalendarPlus,
  CalendarCheck,
  MapPin,
  FileText,
  User,
  Pencil,
  Plus,
  Sparkles,
  UserPlus,
} from 'lucide-react';
import { DealFormDialog } from '@/components/crm/deals/DealFormDialog';
import { CrmCustomer } from '@/hooks/useCrmCustomers';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SocialNetworkBadge } from '@/components/ui/social-network-badge';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { formatPhoneForDisplay } from '@/lib/phone-utils';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { QuickActivityBar, QuickActivityType } from '@/components/crm/deals/modal/components/QuickActivityBar';
import { CustomerActivityFormSheet } from './components/CustomerActivityFormSheet';
import { EditContactDialog } from '@/components/tenant/contacts/EditContactDialog';
import { EditSellerDialog } from './dialogs/EditSellerDialog';
import { EditAddressDialog } from './dialogs/EditAddressDialog';
import { EditNotesDialog } from './dialogs/EditNotesDialog';

// Props do componente
interface CustomerModalSidebarProps {
  customer: CrmCustomer;
  /** Removido: status do cliente é sempre Ativo */
  onContactUpdated?: () => void;
  /**
   * v2.1.0: Modo Contato — contato sem cliente vinculado.
   * Oculta seções exclusivas de cliente (documento, vendedor, endereço,
   * observações e atividades), mantendo identidade + contato + novo negócio.
   */
  isContactMode?: boolean;
  /** v2.2.0: conversão contato → cliente (renderizada só no modo Contato) */
  onConvertToCustomer?: () => void;
}

/**
 * SectionCard — wrapper visual reutilizado em todas as seções
 * (mesmo padrão do DealModalSidebar v3.0.0 / TaskModalSidebar)
 */
function SectionCard({
  title,
  icon: Icon,
  action,
  children,
}: {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Elemento opcional à direita do header (ex.: botão Pencil) */
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h4>
        {action ?? (Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />)}
      </div>
      {children}
    </div>
  );
}

/**
 * Linha de informação rápida (label à esquerda + valor à direita)
 */
function QuickInfoRow({
  icon: Icon,
  label,
  value,
  className,
}: {
  icon: typeof Phone;
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  if (!value) return null;

  return (
    <div className={cn('flex items-center gap-2 py-1.5 w-full', className)}>
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="text-xs font-medium ml-auto truncate max-w-[180px]">{value}</span>
    </div>
  );
}

/** Obtém o nome de exibição do cliente */
function getDisplayName(customer: CrmCustomer): string {
  if (customer.customer_type === 'pf') {
    return customer.nome || 'Sem nome';
  }
  return customer.nome_fantasia || customer.razao_social || 'Sem nome';
}

/** Obtém o documento do cliente (CPF ou CNPJ) */
function getDocument(customer: CrmCustomer): string | null {
  return customer.customer_type === 'pf' ? customer.cpf : customer.cnpj;
}

/** Formata endereço completo */
function formatAddress(customer: CrmCustomer): string | null {
  const parts = [
    customer.logradouro,
    customer.numero,
    customer.complemento,
    customer.bairro,
    customer.cidade,
    customer.estado,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : null;
}

/**
 * Botão de edição padronizado (ícone Pencil/Plus) — sempre visível,
 * igual ao usado no DealModalSidebar para garantir consistência visual.
 */
function EditIconButton({
  onClick,
  title,
  icon: Icon = Pencil,
}: {
  onClick: () => void;
  title: string;
  icon?: typeof Pencil;
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-5 w-5 text-muted-foreground hover:text-foreground"
      onClick={onClick}
      title={title}
    >
      <Icon className="h-3 w-3" />
    </Button>
  );
}

/**
 * Sidebar contextual (360px fixa à DIREITA) — padrão visual unificado
 */
export function CustomerModalSidebar({
  customer,
  
  onContactUpdated,
  isContactMode = false,
  onConvertToCustomer,
}: CustomerModalSidebarProps) {
  // Estados para o sheet de atividade
  const [activitySheetOpen, setActivitySheetOpen] = useState(false);
  const [selectedActivityType, setSelectedActivityType] = useState<QuickActivityType | null>(null);

  // Estados dos dialogs de edição
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [editSellerOpen, setEditSellerOpen] = useState(false);
  const [newDealModalOpen, setNewDealModalOpen] = useState(false);
  const [editAddressOpen, setEditAddressOpen] = useState(false);
  const [editNotesOpen, setEditNotesOpen] = useState(false);

  const displayName = getDisplayName(customer);
  const document = getDocument(customer);
  const address = formatAddress(customer);
  const isPF = customer.customer_type === 'pf';

  /** Handler para clique nos ícones de atividade rápida */
  const handleQuickActivity = (type: QuickActivityType) => {
    if (type === 'scheduling') return; // Scheduling não se aplica a customer
    setSelectedActivityType(type);
    setActivitySheetOpen(true);
  };

  return (
    <>
      {/* Mobile: largura total e sem borda lateral (é injetada dentro da aba Resumo) */}
      <ScrollArea className="w-full md:w-[360px] h-auto md:h-full shrink-0 bg-muted/30 rounded-lg md:rounded-none border md:border-l md:border-t-0 md:border-r-0 md:border-b-0 border-border/40 relative z-10">
        <div className="p-3 space-y-3">
          {/* ═══════════════════════════════════════════ */}
          {/* IDENTIDADE — Avatar + nome + documento     */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard title={isContactMode ? 'Contato' : isPF ? 'Cliente' : 'Empresa'} icon={User}>
            <div className="flex flex-col items-center text-center pt-1">
              <SmartAvatar
                src={customer.main_contact?.avatar_url}
                name={displayName}
                size="lg"
                className="h-16 w-16 mb-2"
              />
              <h3 className="text-sm font-semibold">{displayName}</h3>
              {/* Tipo de pessoa e documento só existem no cadastro de cliente */}
              {!isContactMode && (
                <>
                  <span className="text-xs text-muted-foreground">
                    {isPF ? 'Pessoa Física' : 'Pessoa Jurídica'}
                  </span>
                  {document && (
                    <span className="text-xs text-muted-foreground mt-0.5">
                      {isPF ? 'CPF' : 'CNPJ'}: {formatDocumentForDisplay(document)}
                    </span>
                  )}
                </>
              )}

              {/* v2.3.0: ação "Transformar em cliente" logo abaixo do nome do
                  contato — melhora a leitura dos dados e a descoberta da ação */}
              {isContactMode && onConvertToCustomer && (
                <Button
                  variant="outline"
                  size="sm"
                  /* v2.4.0: outline compacto (mesmo padrão do card do negócio) —
                     o botão preto dominava visualmente a sidebar */
                  className="mt-3 h-7 w-full gap-1.5 text-[11px] font-medium"

                  onClick={onConvertToCustomer}
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  Transformar em cliente
                </Button>
              )}

              {/* Barra de Ações Rápidas (ligar/email/tarefa/nota) — depende de customer_id */}
              {!isContactMode && (
                <QuickActivityBar onActivityClick={handleQuickActivity} className="mt-3" />
              )}

            </div>
          </SectionCard>


          {/* ═══════════════════════════════════════════ */}
          {/* CONTATO PRINCIPAL                          */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard
            title="Contato Principal"
            icon={User}
            action={
              customer.main_contact ? (
                <EditIconButton
                  onClick={() => setEditContactOpen(true)}
                  title="Editar contato"
                />
              ) : undefined
            }
          >
            {customer.main_contact ? (
              <div className="space-y-0.5">
                <QuickInfoRow
                  icon={User}
                  label="Nome"
                  value={customer.main_contact.name || '-'}
                />
                <QuickInfoRow
                  icon={Phone}
                  label="Telefone"
                  value={formatPhoneForDisplay(customer.main_contact.phone) || '-'}
                />
                <QuickInfoRow
                  icon={Mail}
                  label="E-mail"
                  value={customer.main_contact.email || '-'}
                />

                {/* Redes Sociais do Contato */}
                <SocialNetworkBadge
                  channelIds={customer.main_contact?.channel_ids}
                  metadata={customer.main_contact?.metadata}
                  variant="default"
                  showLabel={true}
                />
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Nenhum contato vinculado</p>
            )}
          </SectionCard>

          {/* ═══════════════════════════════════════════ */}
          {/* AÇÕES RÁPIDAS — Novo Negócio               */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard title="Ações Rápidas" icon={Sparkles}>
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-10 text-xs text-muted-foreground border-2 border-dashed border-muted-foreground/30 rounded-lg hover:border-muted-foreground/50 hover:bg-muted/30 transition-colors"
              onClick={() => setNewDealModalOpen(true)}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Novo Negócio
            </Button>

            {/* v2.3.0: ação de conversão movida para o card de identidade
                (abaixo do nome do contato) — evita duplicidade da mesma ação */}

          </SectionCard>

          {/* ═══════════════════════════════════════════ */}
          {/* VENDEDOR — também exibido no modo Contato   */}
          {/* (no modo Contato é somente leitura)         */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard
            title="Vendedor Responsável"
            icon={User}
            action={
              isContactMode ? undefined : (
                <EditIconButton
                  onClick={() => setEditSellerOpen(true)}
                  title="Editar vendedor"
                />
              )
            }
          >
            {customer.seller ? (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                <SmartAvatar
                  src={customer.seller.avatar_url}
                  name={customer.seller.name}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{customer.seller.name}</p>
                  {customer.seller.email && (
                    <p className="text-[10px] text-muted-foreground truncate">
                      {customer.seller.email}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Nenhum vendedor atribuído</p>
            )}
          </SectionCard>

          {/* ═══════════════════════════════════════════ */}
          {/* SEÇÕES EXCLUSIVAS DE CLIENTE                */}
          {/* (endereço e observações)                    */}
          {/* ═══════════════════════════════════════════ */}
          {!isContactMode && (
          <>


          {/* ═══════════════════════════════════════════ */}
          {/* ENDEREÇO                                    */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard
            title="Endereço"
            icon={MapPin}
            action={
              <EditIconButton
                onClick={() => setEditAddressOpen(true)}
                title="Editar endereço"
              />
            }
          >
            {address ? (
              <>
                <div className="flex items-start gap-2">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                  <p className="text-xs">{address}</p>
                </div>
                {customer.cep && (
                  <p className="text-xs text-muted-foreground mt-1 ml-5">
                    CEP: {customer.cep}
                  </p>
                )}
              </>
            ) : (
              <p className="text-xs text-muted-foreground">Nenhum endereço cadastrado</p>
            )}
          </SectionCard>

          {/* ═══════════════════════════════════════════ */}
          {/* OBSERVAÇÕES                                 */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard
            title="Observações"
            icon={FileText}
            action={
              <EditIconButton
                onClick={() => setEditNotesOpen(true)}
                title={customer.notes ? 'Editar observações' : 'Adicionar observação'}
                icon={customer.notes ? Pencil : Plus}
              />
            }
          >
            <div className="flex items-start gap-2">
              <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                {customer.notes || 'Nenhuma observação registrada'}
              </p>
            </div>
          </SectionCard>
          </>
          )}


          {/* ═══════════════════════════════════════════ */}
          {/* DATAS — Cliente desde / Atualizado          */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard title="Datas" icon={Calendar}>
            <div className="space-y-1">
              <div className="flex items-center gap-2 py-1.5 w-full">
                <CalendarPlus className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">
                  {isContactMode ? 'Criado em' : 'Cliente desde'}
                </span>

                <span className="text-xs font-medium ml-auto">
                  {format(new Date(customer.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                </span>
              </div>
              <div className="flex items-center gap-2 py-1.5 w-full">
                <CalendarCheck className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">Atualizado</span>
                <span className="text-xs font-medium ml-auto">
                  {format(new Date(customer.updated_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                </span>
              </div>
            </div>
          </SectionCard>
        </div>
      </ScrollArea>

      {/* Sheet de formulário de atividade (somente cliente) */}
      {!isContactMode && (
        <CustomerActivityFormSheet
          open={activitySheetOpen}
          onOpenChange={setActivitySheetOpen}
          activityType={selectedActivityType}
          customerId={customer.id}
        />
      )}


      {/* Dialog de edição do contato principal */}
      {customer.main_contact && (
        <EditContactDialog
          open={editContactOpen}
          onOpenChange={setEditContactOpen}
          contact={{
            id: customer.main_contact.id,
            name: customer.main_contact.name,
            phone: customer.main_contact.phone,
            email: customer.main_contact.email,
            avatar_url: customer.main_contact.avatar_url,
            seller_id: customer.seller?.id || null,
          }}
          onSuccess={() => {
            onContactUpdated?.();
          }}
        />
      )}

      {/* Dialogs exclusivos de cliente (vendedor / endereço) */}
      {!isContactMode && (
        <>
          <EditSellerDialog
            open={editSellerOpen}
            onOpenChange={setEditSellerOpen}
            customerId={customer.id}
            currentSellerId={customer.seller?.id}
            onSuccess={onContactUpdated}
          />

          <EditAddressDialog
            open={editAddressOpen}
            onOpenChange={setEditAddressOpen}
            customer={customer}
            onSuccess={onContactUpdated}
          />
        </>
      )}


      {/* Modal de criação de novo negócio
          v: montado apenas quando aberto — evita carregar o catálogo de
          produtos (get_tenant_products_safe) já na abertura do modal */}
      {newDealModalOpen && (
        <DealFormDialog
          open={newDealModalOpen}
          onOpenChange={setNewDealModalOpen}
          defaultContactId={customer.main_contact?.id}
          onSuccess={() => {
            // v3.5.0: fechamento controlado pelo formulário (modal de sucesso)
            onContactUpdated?.();
          }}
        />
      )}

      {/* Dialog de edição de observações (somente cliente) */}
      {!isContactMode && (
        <EditNotesDialog
          open={editNotesOpen}
          onOpenChange={setEditNotesOpen}
          customer={customer}
          onSuccess={onContactUpdated}
        />
      )}

    </>
  );
}
